import styled from "@emotion/styled";
import { useEffect, useState } from "react";
import {
  BoardCard,
  Board as boardInterface,
  stages,
} from "../../../../types/board";
import Board from "../../../trello-board/Board";
import { useDispatch } from "react-redux";
import {
  setBusinessUnitFilters,
  updateCustomBoardStage,
} from "../../../../redux/features/custom-board/custom-board-slice";
import { moveClientCardToAnotherBoard } from "../../../../redux/features/client-main/client-board-slice";
import {
  Checkbox,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { OrgBU } from "src/types/company";
import { useAppSelector } from "src/redux/store";
import { GetAllProviders } from "src/redux/features/providers-main/provider-list-slice";
import { DragDropContext, DropResult } from "@hello-pangea/dnd";
import { setOnboardingStage } from "src/redux/features/providers-main/provider-onboardingstage-slice";
const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

interface ClientBoardProps {
  boardInfo: boardInterface;
  callbacks: ((clientId: string) => Promise<void>)[];
  chatCommand: (id: string) => void;
}

const Boards = styled.div`
  display: flex;
  flex-grow: 1;
  align-items: baseline;
`;

const ManualBoard: React.FC<ClientBoardProps> = ({
  boardInfo,
  callbacks,
  chatCommand,
}) => {
  //#region variable region

  const [boardStage, setboardStage] = useState<stages[]>(
    boardInfo.stages || []
  );

  //get all clients
  const clientList = useAppSelector((state) => state.clientListSlice.listData);

  //get all provider
  const providerList = useAppSelector(
    (state) => state.providerListSlice.listData
  );

  const businessUnits = useAppSelector(
    (state) => state.businessUnitsSlice.businessUnits
  );

  const businessUnitsFilterDetails = useAppSelector(
    (state) => state.boardSlice.selectedBusinessUnits
  );

  const selectedBusinessUnits =
    businessUnitsFilterDetails?.find((item) => item.boardId === boardInfo.id)
      ?.businessUnits || [];

  //handle dispatch
  const dispatch = useDispatch();
  //#endregion

  //#region methods region
  const removeFocus = (id: string) => {
    document.getElementById(id)?.blur();
  };

  // Helper function to find stage by droppableId (handles fallback values)
  const findStageByDroppableId = (droppableId: string) => {
    return boardInfo.stages.find((stage) => {
      // First try to match by id
      if (stage.id === droppableId) return true;
      // Then try to match by stageName
      if (stage.stageName === droppableId) return true;
      // Finally try to match by fallback format
      if (`stage-${stage.index}` === droppableId) return true;
      return false;
    });
  };

  const handleCrossStageDrop = (
    dragStageId: string,
    dropStageId: string,
    cardId: string,
    dropIndex: number
  ) => {
    console.log("Cross stage drop:", {
      dragStageId,
      dropStageId,
      cardId,
      dropIndex,
    });

    // Find the source and destination stages
    const sourceStage = findStageByDroppableId(dragStageId);
    const destStage = findStageByDroppableId(dropStageId);

    if (!sourceStage || !destStage) {
      console.error("Source or destination stage not found");
      return;
    }

    // Find the card in the source stage
    const cardIndex = sourceStage.boardColumnNames.findIndex(
      (card) => card.id === cardId
    );
    if (cardIndex === -1) {
      console.error("Card not found in source stage");
      return;
    }

    const cardToMove = sourceStage.boardColumnNames[cardIndex];

    // Create updated stages
    const updatedStages = boardInfo.stages.map((stage) => {
      if (stage.id === sourceStage.id) {
        // Remove card from source stage
        const updatedCards = [...stage.boardColumnNames];
        updatedCards.splice(cardIndex, 1);
        return { ...stage, boardColumnNames: updatedCards };
      } else if (stage.id === destStage.id) {
        // Add card to destination stage at the specified index
        const updatedCards = [...stage.boardColumnNames];
        updatedCards.splice(dropIndex, 0, cardToMove);
        return { ...stage, boardColumnNames: updatedCards };
      }
      return stage;
    });

    // Update Redux state
    dispatch(
      updateCustomBoardStage({
        updatedStages: updatedStages,
        boardId: boardInfo.id,
      })
    );

       // Make API call
    dispatch(
      moveClientCardToAnotherBoard({
        fromStageId: sourceStage.id,
        tostageId: destStage.id,
        cardId: cardId,
        index: dropIndex,
        boardType: "custom",
        boardId: boardInfo.id
      })
    );
  };

  // Handle same-stage reordering
  const handleSameStageReorder = (
    stageId: string,
    cardId: string,
    fromIndex: number,
    toIndex: number
  ) => {
    console.log("Same stage reorder:", { stageId, cardId, fromIndex, toIndex });

    const stage = findStageByDroppableId(stageId);
    if (!stage) {
      console.error("Stage not found");
      return;
    }

    // Create updated stage with reordered cards
    const updatedCards = [...stage.boardColumnNames];
    const [movedCard] = updatedCards.splice(fromIndex, 1);
    updatedCards.splice(toIndex, 0, movedCard);

    const updatedStage = { ...stage, boardColumnNames: updatedCards };

    // Update Redux state
    dispatch(
      updateCustomBoardStage({
        updatedStages: [updatedStage],
        boardId: boardInfo.id,
      })
    );

    // Make API call only on drop
    dispatch(
      moveClientCardToAnotherBoard({
        fromStageId: stageId,
        tostageId: stageId,
        cardId: cardId,
        index: toIndex,
        boardType: "custom",
        boardId: boardInfo.id
      })
    );
  };

  // Handle drag end for hello-pangea-dnd
  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return;
    }

    const { source, destination, draggableId } = result;
    const sourceStage = source.droppableId;
    const destinationStage = destination.droppableId;
    const sourceIndex = source.index;
    const destinationIndex = destination.index;

    // Handle same-stage reordering
    if (sourceStage === destinationStage) {
      handleSameStageReorder(
        sourceStage,
        draggableId,
        sourceIndex,
        destinationIndex
      );
    } else {
      // Handle cross-stage drops
      handleCrossStageDrop(
        sourceStage,
        destinationStage,
        draggableId,
        destinationIndex
      );
    }
  };
  //#endregion
  //#region useEffect region
  useEffect(() => {
    setboardStage(boardInfo?.stages || []);
  }, [boardInfo.stages, boardInfo]);
  // business unit filtering
  useEffect(() => {
    if (!boardInfo) {
      return;
    }
    if (selectedBusinessUnits.length > 0) {
      // Common logic to filter entity IDs based on business units
      const filteredEntityIds =
        boardInfo.boardType === "client"
          ? clientList
              .filter((client) =>
                selectedBusinessUnits.some((bu: OrgBU) =>
                  client.clientBasicDetails.businessUnit.includes(bu.id)
                )
              )
              .map((client) => client.clientBasicDetails.id)
          : providerList
              .filter((provider) =>
                selectedBusinessUnits.some((bu: OrgBU) =>
                  provider.employmentDetails.businessUnit.includes(bu.id)
                )
              )
              .map((provider) => provider.employmentDetails.id);

      // Reduce tasks based on filtered entity IDs
      const filteredStages = boardInfo.stages.map((stage) => ({
        ...stage,
        boardColumnNames: stage.boardColumnNames.filter(
          (card: BoardCard) =>
            card.entityId && filteredEntityIds.includes(card.entityId)
        ),
      }));
      setboardStage(filteredStages);
    } else {
      setboardStage(boardInfo.stages);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBusinessUnits.length, boardInfo, clientList, providerList]);

  useEffect(() => {
    if (boardInfo.boardType === "provider" && providerList.length === 0) {
      dispatch(GetAllProviders("full"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerList.length, boardInfo]);
  //#endregion

  return (
    <>
      <div className="taskHeader">
        <Typography>Filter:</Typography>
        <div className="taskFormController">
          <FormControl className="task-outer">
            <InputLabel id="demo-simple-select-label">
              {"Business Unit"}
            </InputLabel>
            <Select
              className="form-control bufilterDropdown"
              labelId="demo-simple-select-label"
              id="demo-simple-select"
              style={{ width: "250px" }}
              multiple
              value={selectedBusinessUnits.map((bu) => bu.id)}
              onChange={(e) => {
                const selectedIds = e.target.value as string[];
                const newSelectedBU = businessUnits.filter((item: OrgBU) =>
                  selectedIds.includes(item.id)
                );
                dispatch(
                  setBusinessUnitFilters({
                    boardId: boardInfo.id,
                    businessUnits: newSelectedBU,
                  })
                );
              }}
              renderValue={(selected: string[]) =>
                businessUnits
                  .filter((bu: OrgBU) => selected.includes(bu.id))
                  .map((b) => b.businessUnitName)
                  .join(", ")
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  removeFocus("demo-simple-select");
                }
              }}
              MenuProps={MenuProps}
            >
              {businessUnits?.map((item: OrgBU) => (
                <MenuItem key={item.id} value={item.id}>
                  <Checkbox checked={selectedBusinessUnits.includes(item)} />
                  {item.businessUnitName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
      </div>

        <Boards>
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="allStageCards">
              {boardStage.map((stage, index) => {
                return (
                  <Board
                    stageId={stage.id}
                    key={stage.stageName}
                    stage={stage}
                    callbacks={callbacks}
                    chatCommand={chatCommand}
                  />
                );
              })}
            </div>
          </DragDropContext>
        </Boards>
 
    </>
  );
};

export default ManualBoard;
